import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import {
    requiredValueConfig,
    conditionalEmailValidationConfig,
    countrySpecificOrganizationNumberValidationConfig,
    organizationNumberFormatHint,
    allowedUrlsValidationConfig,
} from '../../../../types/ValidationTypes';
import { parseUrlLines } from '../../../../services/helpers/urlList';
import Spinner from '../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import FormRow from '../../../atoms/Form/FormRow';
import GridInput from '../../../atoms/Input/GridInput';
import GridTextArea from '../../../atoms/Input/GridTextArea';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import SuccessMessage from '../../../atoms/Message/Success/SuccessMessage';
import Button from '../../../atoms/Button/Button';
import SelectInput from '../../../atoms/Input/SelectInput';
import Toggle from '../../../atoms/Toggle/Toggle';
import { merchantCountryOptions } from '../Merchants/merchantCountryOptions';
import { CreateOrganizationRequest } from '../../../../types/Organization';
import { useOrganizationsStore } from '../../../../store/admin/organizations/organizationsStore';

interface CreateOrganizationFormProps {
    onCreate: (request: CreateOrganizationRequest) => void;
    hide: () => void;
    theme: DefaultTheme;
}

type Inputs = {
    name: string;
    merchantName: string;
    organizationNumber: string;
    /** One https:// URL per line; parsed into the allowed URLs of the merchant on submit. */
    allowedUrls: string;
    adminEmail: string;
};

export const allowedUrlsHint =
    'One https:// address per line, without a path, such as https://shop.example.se. The first one becomes the website of the merchant, and a store can only be onboarded on one of them.';

const CreateOrganizationForm: React.FC<CreateOrganizationFormProps> = ({
    onCreate,
    hide,
    theme,
}: CreateOrganizationFormProps) => {
    const {
        isCreatingOrganization,
        isCreatedOrganization,
        createOrganizationError,
        addMerchantError,
        inviteAdministratorError,
        createdOrganizationName,
    } = useOrganizationsStore();

    const [country, setCountry] = useState('SE');
    const [sendInvitation, setSendInvitation] = useState(true);

    const form = useForm<Inputs>({
        mode: 'onSubmit',
        shouldUseNativeValidation: true,
        defaultValues: {
            name: '',
            merchantName: '',
            organizationNumber: '',
            allowedUrls: '',
            adminEmail: '',
        },
    });
    const {
        getValues,
        register,
        handleSubmit,
        trigger,
        formState: { isSubmitted },
    } = form;

    const name = register('name', requiredValueConfig);
    const merchantName = register('merchantName', requiredValueConfig);
    const organizationNumber = register(
        'organizationNumber',
        countrySpecificOrganizationNumberValidationConfig(country)
    );
    const allowedUrls = register('allowedUrls', allowedUrlsValidationConfig);
    const adminEmail = register('adminEmail', conditionalEmailValidationConfig(sendInvitation));

    // The email rules depend on the toggle, but a validation error already shown (natively, on the input) is only
    // re-evaluated when the field itself changes. Re-validate after a submit so toggling the invitation off clears
    // a "Required field" error, and toggling it back on brings it back.
    useEffect(() => {
        if (isSubmitted) trigger('adminEmail');
    }, [sendInvitation, isSubmitted, trigger]);

    const isDisabled = isCreatingOrganization || isCreatedOrganization;

    const onSubmit = (data: Inputs) => {
        onCreate({
            name: data.name,
            merchantName: data.merchantName,
            countryCode: country,
            organizationNumber: data.organizationNumber,
            allowedUrls: parseUrlLines(data.allowedUrls),
            adminEmail: data.adminEmail,
            sendInvitation: sendInvitation,
        });
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Create organization</HeaderTitle>
            </ModalContainer>

            <ModalContainer position="content" noScroll={false}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    data-testid="form"
                    id="create-organization-form"
                >
                    <InputContainer>
                        <FormRow first last>
                            <GridInput
                                type="text"
                                id="name"
                                placeholder="Organization name"
                                placeholderRaised={!!getValues(name.name)}
                                setRef={name.ref}
                                name={name.name}
                                onChange={name.onChange}
                                disabled={isDisabled}
                                dataTestId="organization-name"
                            />
                        </FormRow>
                        <h3>Merchant</h3>
                        <SubtleText>
                            The legal entity registered under the organization number.
                        </SubtleText>
                        <FormRow first>
                            <GridInput
                                type="text"
                                id="merchantName"
                                placeholder="Merchant legal name"
                                placeholderRaised={!!getValues(merchantName.name)}
                                setRef={merchantName.ref}
                                name={merchantName.name}
                                onChange={merchantName.onChange}
                                disabled={isDisabled}
                                dataTestId="merchant-name"
                            />
                        </FormRow>
                        <FormRow last>
                            <SelectInput
                                id="country"
                                dataTestId="create-organization-country-selector"
                                placeholder="Country"
                                onChange={setCountry}
                                value={country}
                                options={merchantCountryOptions}
                                disabled={isDisabled}
                                setHeight={false}
                            />
                            <GridInput
                                type="text"
                                id="organizationNumber"
                                placeholder="Organization number"
                                placeholderRaised={!!getValues(organizationNumber.name)}
                                setRef={organizationNumber.ref}
                                name={organizationNumber.name}
                                onChange={organizationNumber.onChange}
                                disabled={isDisabled}
                                dataTestId="organization-number"
                            />
                        </FormRow>
                        <Hint data-testid="organization-number-hint">
                            {organizationNumberFormatHint(country)}
                        </Hint>
                        <UrlsContainer>
                            <FormRow first last>
                                <GridTextArea
                                    id="allowedUrls"
                                    placeholder="Allowed store URLs"
                                    placeholderRaised={!!getValues(allowedUrls.name)}
                                    setRef={allowedUrls.ref}
                                    name={allowedUrls.name}
                                    onChange={allowedUrls.onChange}
                                    disabled={isDisabled}
                                    dataTestId="allowed-urls"
                                />
                            </FormRow>
                            <Hint>{allowedUrlsHint}</Hint>
                        </UrlsContainer>
                        <h3>Invite an administrator</h3>
                        <SubtleText>
                            This person gets full access to the new organization.
                        </SubtleText>
                        <FormRow first last>
                            <GridInput
                                type="text"
                                id="adminEmail"
                                placeholder={
                                    sendInvitation ? 'Email address' : 'Email address (optional)'
                                }
                                placeholderRaised={!!getValues(adminEmail.name)}
                                setRef={adminEmail.ref}
                                name={adminEmail.name}
                                onChange={adminEmail.onChange}
                                disabled={isDisabled}
                                dataTestId="admin-email"
                            />
                        </FormRow>
                        <ToggleContainer>
                            <Toggle
                                id="sendInvitation"
                                data-testid="send-invitation-toggle"
                                checked={sendInvitation}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setSendInvitation(e.target.checked)
                                }
                                disabled={isDisabled}
                            />
                            <label htmlFor="sendInvitation">Send the invitation now</label>
                        </ToggleContainer>
                    </InputContainer>

                    {isCreatedOrganization && (
                        <ResultContainer>
                            <SuccessMessage>
                                Organization {createdOrganizationName} was created!
                            </SuccessMessage>
                        </ResultContainer>
                    )}
                </form>
            </ModalContainer>

            {(createOrganizationError || addMerchantError || inviteAdministratorError) && (
                <ModalContainer position="error">
                    {createOrganizationError && (
                        <ErrorMessage
                            error={createOrganizationError}
                            errorHeader={ErrorType.CreateOrganization}
                        />
                    )}
                    {addMerchantError && (
                        <ErrorMessage
                            error={addMerchantError}
                            errorHeader={ErrorType.AddMerchantToOrganization}
                        />
                    )}
                    {inviteAdministratorError && (
                        <ErrorMessage
                            error={inviteAdministratorError}
                            errorHeader={ErrorType.InviteToCreatedOrganization}
                        />
                    )}
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    {!isCreatedOrganization && (
                        <Button onClick={hide} large>
                            Cancel
                        </Button>
                    )}
                    {!isCreatedOrganization && (
                        <Button
                            tabIndex={0}
                            form="create-organization-form"
                            type="submit"
                            large
                            primary
                            disabled={isDisabled}
                            data-testid="create-organization-submit-button"
                        >
                            {isCreatingOrganization ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                <span>Create organization</span>
                            )}
                        </Button>
                    )}
                    {isCreatedOrganization && (
                        <Button onClick={hide} large primary>
                            Close
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

const InputContainer = styled.div`
    display: block;
    padding-bottom: ${props => props.theme.layout.padding.small};
`;

const ResultContainer = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: center;
    padding-bottom: 2.5rem;
`;

const Hint = styled.div`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    margin-top: 0.5rem;
`;

const SubtleText = styled.div`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    margin-bottom: 1rem;
`;

const UrlsContainer = styled.div`
    margin-top: 1.5rem;
`;

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;

    label {
        cursor: pointer;
    }
`;

export default withTheme(CreateOrganizationForm) as React.ComponentType<
    Omit<CreateOrganizationFormProps, 'theme'>
>;
